import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Fade,
  alpha,
  Chip,
  Snackbar,
} from '@mui/material';
import {
  Edit,
  Delete,
  Visibility,
  Code,
  Add,
  Widgets,
  Store,
  Category,
  Inventory,
  AutoAwesome,
  ContentCopy,
  Refresh,
  TableChart,
} from '@mui/icons-material';
import PageHeader from '../components/common/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import StatusBadge from '../components/common/StatusBadge';
import SearchInput from '../components/common/SearchInput';
import EmptyState from '../components/common/EmptyState';
import { CardSkeleton } from '../components/common/LoadingState';
import api from '../services/api';

const WidgetsPage = () => {
  const navigate = useNavigate();
  const [widgets, setWidgets] = useState([]);
  const [retails, setRetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [filterRetail, setFilterRetail] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [copySnackbar, setCopySnackbar] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [widgetsRes, retailsRes] = await Promise.all([
        api.get('/widgets'),
        api.get('/retails'),
      ]);
      setWidgets(widgetsRes.data);
      setRetails(retailsRes.data);
    } catch (err) {
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async () => {
    try {
      await api.delete(`/widgets/${selectedWidget.id}`);
      setDeleteDialogOpen(false);
      setSelectedWidget(null);
      setSuccess('Widget eliminado exitosamente');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar widget');
    }
  };

  const getRetailName = (retailId) => {
    const retail = retails.find(r => r.id === retailId);
    return retail?.name || '-';
  };

  let filteredWidgets = widgets;
  if (filterRetail) {
    filteredWidgets = filteredWidgets.filter(w => w.retailId === filterRetail);
  }
  if (searchTerm) {
    filteredWidgets = filteredWidgets.filter(w =>
      w.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  const copyEndpoint = (widget) => {
    const endpoint = `${window.location.origin}/api/widgets/render/${widget.id}`;
    navigator.clipboard.writeText(endpoint);
    setCopySnackbar(true);
  };

  const getColorByIndex = (index) => {
    const colors = ['primary', 'secondary', 'success', 'warning', 'info'];
    return colors[index % colors.length];
  };

  return (
    <Box>
      <PageHeader
        title="Widgets de Upselling"
        subtitle="Configura widgets para mostrar productos relacionados"
        actionLabel="Nuevo Widget"
        actionIcon={<Add />}
        onAction={() => navigate('/widgets/new')}
        badge={widgets.length}
        badgeColor="warning"
      >
        <Tooltip title="Refrescar lista">
          <IconButton onClick={fetchData}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </PageHeader>

      {error && (
        <Fade in>
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        </Fade>
      )}
      {success && (
        <Fade in>
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        </Fade>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar widgets..."
          sx={{ minWidth: 280 }}
        />
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Filtrar por Retail</InputLabel>
          <Select
            value={filterRetail}
            label="Filtrar por Retail"
            onChange={(e) => setFilterRetail(e.target.value)}
          >
            <MenuItem value="">Todos los retails</MenuItem>
            {retails.map((retail) => (
              <MenuItem key={retail.id} value={retail.id}>
                {retail.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <CardSkeleton count={6} />
      ) : filteredWidgets.length === 0 ? (
        <Card>
          <EmptyState
            icon={Widgets}
            title="No hay widgets configurados"
            description="Crea tu primer widget de upselling para comenzar a mostrar productos relacionados"
            actionLabel="Crear Widget"
            onAction={() => navigate('/widgets/new')}
            size="large"
            sx={{ py: 8 }}
          />
        </Card>
      ) : (
        <Grid container spacing={3}>
          {filteredWidgets.map((widget, index) => (
            <Grid item xs={12} sm={6} lg={4} key={widget.id}>
              <Fade in timeout={300} style={{ transitionDelay: `${index * 50}ms` }}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'visible',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      borderRadius: '12px 12px 0 0',
                      background: (theme) => {
                        const color = theme.palette[getColorByIndex(index)].main;
                        return `linear-gradient(90deg, ${color} 0%, ${alpha(color, 0.5)} 100%)`;
                      },
                    },
                  }}
                >
                  <CardContent sx={{ flex: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {widget.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Store sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {widget.retailName || getRetailName(widget.retailId)}
                          </Typography>
                        </Box>
                      </Box>
                      <StatusBadge
                        status={widget.isActive ? 'active' : 'inactive'}
                        showIcon={false}
                        size="small"
                      />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {widget.categoryName && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Category sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {widget.categoryName}
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', gap: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Inventory sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            <strong>{widget.productSkus?.length || 0}</strong>
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                              productos
                            </Typography>
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Code sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            <strong>{widget.selectedFields?.length || 0}</strong>
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                              campos
                            </Typography>
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          icon={<AutoAwesome sx={{ fontSize: '14px !important' }} />}
                          label={widget.orderMode === 'ai' ? 'IA' : 'Manual'}
                          size="small"
                          color={widget.orderMode === 'ai' ? 'secondary' : 'default'}
                          sx={{ height: 24 }}
                        />
                        {widget.brands && widget.brands.length > 0 && widget.brands.slice(0, 2).map((brand, i) => (
                          <Chip key={i} label={brand} size="small" variant="outlined" sx={{ height: 24 }} />
                        ))}
                        {widget.brands && widget.brands.length > 2 && (
                          <Chip
                            label={`+${widget.brands.length - 2}`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 24 }}
                          />
                        )}
                      </Box>
                    </Box>
                  </CardContent>

                  <CardActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', gap: 0.5 }}>
                    <Tooltip title="Ver Widget" arrow>
                      <IconButton
                        size="small"
                        onClick={() => window.open(`/api/widgets/render/${widget.id}`, '_blank')}
                        sx={{
                          color: 'info.main',
                          '&:hover': {
                            backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08),
                          },
                        }}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copiar Endpoint" arrow>
                      <IconButton
                        size="small"
                        onClick={() => copyEndpoint(widget)}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': {
                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                            color: 'primary.main',
                          },
                        }}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editor de Comparacion" arrow>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/widgets/${widget.id}/editor`)}
                        sx={{
                          color: 'secondary.main',
                          '&:hover': {
                            backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.08),
                          },
                        }}
                      >
                        <TableChart fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title="Editar" arrow>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/widgets/${widget.id}/edit`)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar" arrow>
                      <IconButton
                        size="small"
                        sx={{
                          color: 'error.main',
                          '&:hover': {
                            backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
                          },
                        }}
                        onClick={() => {
                          setSelectedWidget(widget);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Eliminar Widget"
        message={`¿Estás seguro de que deseas eliminar el widget "${selectedWidget?.name}"? Esta acción no se puede deshacer.`}
        type="delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />

      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        message="Endpoint copiado al portapapeles"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default WidgetsPage;
